import { ValuesType } from 'utility-types';

import { GAME_MODE } from '~/interfaces';
import styles from '~/modules/game-engine/drawing/styles';

/** The figure's own proportions, in the SVG's coordinate space. */
const FIGURE_WIDTH = 24;
const FIGURE_HEIGHT = 32;

interface Props {
  /** One figure per entry, filled with that colour. */
  colors: string[];
  /** Rendered height in px; the width follows from the number of figures. */
  height?: number;
  className?: string;
}

/**
 * A row of little people, coloured like the players they stand for. Says how many are singing and
 * in whose colours at a glance, which a mode's name alone does not - "Pass The Mic" and
 * "Cooperation" both read as "more than one person" until you already know the game.
 */
export default function PeopleGlyph({ colors, height = FIGURE_HEIGHT, className }: Props) {
  const scale = height / FIGURE_HEIGHT;

  return (
    <svg
      width={FIGURE_WIDTH * colors.length * scale}
      height={height}
      viewBox={`0 0 ${FIGURE_WIDTH * colors.length} ${FIGURE_HEIGHT}`}
      aria-hidden
      className={`block shrink-0 ${className ?? ''}`}>
      {colors.map((color, index) => (
        <g key={index} transform={`translate(${index * FIGURE_WIDTH} 0)`} fill={color}>
          <circle cx="12" cy="8" r="6.5" />
          <path d="M1.5 31 C1.5 21 6 17 12 17 C18 17 22.5 21 22.5 31 Z" />
        </g>
      ))}
    </svg>
  );
}

/**
 * Co-op is two figures in the *same* colour - the pair share one score, which is the whole
 * difference between it and a duel.
 */
const colorsByMode = (): Record<ValuesType<typeof GAME_MODE>, string[]> => {
  const players = styles.colors.players;

  return {
    [GAME_MODE.DUEL]: [players[0].text, players[1].text],
    [GAME_MODE.CO_OP]: [players[0].text, players[0].text],
    [GAME_MODE.PASS_THE_MIC]: [players[0].text, players[1].text, players[2].text],
  };
};

interface GameModeIconProps {
  mode: ValuesType<typeof GAME_MODE>;
  height?: number;
  className?: string;
}

export function GameModeIcon({ mode, height, className }: GameModeIconProps) {
  return <PeopleGlyph colors={colorsByMode()[mode]} height={height} className={className} />;
}
